// components/WorkspaceTaskMenu.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Copy, ListChecks, Loader2 } from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import type { GenerationTask, KernelHandle } from '#types/types';
import { describeGenerationFailure } from '#lib/failed-generation';
import {
  classifyGenerationTasks,
  formatTaskElapsed,
  generationTaskElapsedSeconds,
  resolveGenerationTaskModel,
  type GenerationTaskStatus,
} from '#lib/generation-task-status';
import { isInertSurface } from '#lib/keyboard-shortcuts';
import { STUDIO_LIVE_VIEWPORT_VARS_RESET } from '#lib/studio-viewport';

const BUTTON_SIZE = 28;
const BUTTON_STYLE = { width: BUTTON_SIZE, height: BUTTON_SIZE, padding: 0 } as const;
const STATUS_LABEL: Record<GenerationTaskStatus, string> = { running: 'Running', finished: 'Finished', errored: 'Errored' };
const SECTIONS: GenerationTaskStatus[] = ['running', 'finished', 'errored'];
const stopEventPropagation = (event: React.SyntheticEvent) => event.stopPropagation();

export const openGenerationTaskContextMenu = (
  kernel: RefObject<KernelHandle | null>,
  task: GenerationTask,
  position: { x: number; y: number },
) => {
  const handleCopyDebugInfo = () => {
    try {
      const { layer, maskFile, layerContextSnapshot, ...debugInfo } = task;
      navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2));
      toast.success('Debug info copied to clipboard.');
    } catch {
      kernel.current?.handleError?.('Failed to copy debug info.');
    }
    kernel.current?.setMenuData(null);
  };

  const handleRemove = () => {
    const projectId = typeof task.projectId === 'string' ? task.projectId.trim() : '';
    if (!projectId) {
      kernel.current?.handleError?.('Cannot remove task: missing project scope.');
      kernel.current?.setMenuData(null);
      return;
    }
    kernel.current?.setGenerationTasks(projectId, prevTasks => prevTasks.filter(t => t.id !== task.id));
    kernel.current?.setMenuData(null);
  };

  kernel.current?.setMenuData({
    position,
    actions: [
      { label: 'Remove This Task', onClick: handleRemove },
      { label: 'Copy Debug Info', onClick: handleCopyDebugInfo },
    ],
  });
};

interface TaskRowProps {
  task: GenerationTask;
  status: GenerationTaskStatus;
  isExpanded: boolean;
  onToggle: (taskId: string) => void;
}

const TaskRow = React.memo(({ task, status, isExpanded, onToggle }: TaskRowProps) => {
  const { kernel } = useKernel();
  const [nowMs, setNowMs] = useState(() => Date.now());
  const isTicking = status === 'running' && typeof task.startedAt === 'number';
  const { label: modelLabel } = resolveGenerationTaskModel(task.model);
  const elapsedSeconds = generationTaskElapsedSeconds(task, nowMs);
  const errorText = status === 'errored' ? describeGenerationFailure(task.error) : null;
  const prompt = typeof task.params?.prompt === 'string' ? task.params.prompt.trim() : '';

  useEffect(() => {
    if (!isTicking) return;
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [isTicking]);

  const handleClick = useCallback(() => onToggle(task.id), [onToggle, task.id]);

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    openGenerationTaskContextMenu(kernel, task, { x: event.clientX, y: event.clientY });
  }, [kernel, task]);

  const handleCopyPrompt = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    navigator.clipboard.writeText(prompt).then(
      () => toast.success('Prompt copied.'),
      () => kernel.current?.handleError?.('Failed to copy the prompt.'),
    );
  }, [kernel, prompt]);

  return (
    <div className={`rounded-lg transition-colors duration-150 ease-out ${isExpanded ? 'bg-white/[0.08]' : 'hover:bg-white/[0.06]'}`}>
      <button
        type="button"
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        title={errorText ?? undefined}
        aria-expanded={isExpanded}
        className="flex w-full items-center gap-2 px-2 py-1.5 text-left"
      >
        {status === 'running' && <Loader2 size={12} className="shrink-0 animate-spin text-white/60" />}
        {status === 'errored' && <AlertTriangle size={12} className="shrink-0 text-red-400" />}
        {status === 'finished' && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-white/40" />}
        <span className="min-w-0 flex-1 truncate text-xs text-white/85">{modelLabel}</span>
        {elapsedSeconds !== null && (
          <span className="shrink-0 font-mono text-[10px] tabular-nums text-white/45">{formatTaskElapsed(elapsedSeconds)}</span>
        )}
        <span className={`shrink-0 text-[10px] ${status === 'errored' ? 'text-red-400' : 'text-white/45'}`}>{STATUS_LABEL[status]}</span>
      </button>
      {isExpanded && (
        <div className="flex animate-[fadeIn_140ms_ease-out_both] flex-col gap-1.5 px-2 pb-2 text-[11px] text-white/70">
          {errorText && <div className="text-red-300">{errorText}</div>}
          {prompt ? (
            <div className="flex items-start gap-1.5">
              <p className="max-h-32 min-w-0 flex-1 overflow-y-auto whitespace-pre-wrap break-words select-text text-white/80">{prompt}</p>
              <button
                type="button"
                onClick={handleCopyPrompt}
                className="shrink-0 rounded-md p-1 text-white/50 transition-colors duration-150 ease-out hover:bg-white/10 hover:text-white"
                title="Copy prompt"
                aria-label="Copy prompt"
              >
                <Copy size={12} />
              </button>
            </div>
          ) : (
            <div className="text-white/40">No prompt</div>
          )}
        </div>
      )}
    </div>
  );
});
TaskRow.displayName = 'TaskRow';

interface WorkspaceTaskMenuProps {
  tasks: GenerationTask[];
  frameScreenRight: string;
  frameScreenBottom: string;
}

const WorkspaceTaskMenu = React.memo(({ tasks, frameScreenRight, frameScreenBottom }: WorkspaceTaskMenuProps) => {
  const { kernel } = useKernel();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const groups = useMemo(() => classifyGenerationTasks(tasks), [tasks]);
  const runningCount = groups.running.length;
  const erroredCount = groups.errored.length;

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || isInertSurface(kernel.current?.canvasContainerRef.current)) return;
      event.preventDefault();
      event.stopPropagation();
      setIsOpen(false);
    };
    // The canvas listens to the wheel natively on its container, which React's stopPropagation cannot reach.
    const popover = popoverRef.current;
    const handleWheel = (event: WheelEvent) => event.stopPropagation();
    document.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('keydown', handleKeyDown, true);
    popover?.addEventListener('wheel', handleWheel);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('keydown', handleKeyDown, true);
      popover?.removeEventListener('wheel', handleWheel);
    };
  }, [isOpen, kernel]);

  const handleButtonClick = useCallback(() => setIsOpen(open => !open), []);
  const handleToggleTask = useCallback((taskId: string) => {
    setExpandedTaskId(current => (current === taskId ? null : taskId));
  }, []);

  const rootStyle = useMemo<React.CSSProperties>(() => ({
    position: 'absolute',
    left: `calc(${frameScreenRight} + 8px)`,
    top: `calc(${frameScreenBottom} - ${BUTTON_SIZE}px)`,
    zIndex: isOpen ? 40 : 15,
    pointerEvents: 'auto',
  }), [frameScreenRight, frameScreenBottom, isOpen]);

  const buttonLabel = `${runningCount} running task${runningCount === 1 ? '' : 's'}${erroredCount > 0 ? `, ${erroredCount} errored` : ''}`;

  return (
    <div
      ref={rootRef}
      style={rootStyle}
      onMouseDown={stopEventPropagation}
      onTouchStart={stopEventPropagation}
      onClick={stopEventPropagation}
      onDoubleClick={stopEventPropagation}
      onContextMenu={stopEventPropagation}
    >
      <button
        type="button"
        onClick={handleButtonClick}
        className={`relative flex items-center justify-center rounded-full border border-white/10 text-white transition-colors duration-150 ease-out hover:bg-[#353535] ${isOpen ? 'bg-[#353535]' : 'bg-[#2A2A2A]'}`}
        style={BUTTON_STYLE}
        title={buttonLabel}
        aria-label={buttonLabel}
        aria-expanded={isOpen}
      >
        {runningCount > 0 ? (
          <>
            <Loader2 size={BUTTON_SIZE - 6} strokeWidth={1.5} className="pointer-events-none absolute inset-0 m-auto animate-spin text-white/40" />
            <span className="pointer-events-none relative text-[11px] font-semibold text-white">{runningCount}</span>
          </>
        ) : (
          <ListChecks size={14} className="pointer-events-none text-white/60" />
        )}
        {erroredCount > 0 && (
          <span className="pointer-events-none absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-semibold leading-none text-white">
            {erroredCount}
          </span>
        )}
      </button>
      {isOpen && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label="Workspace generation tasks"
          className="absolute bottom-0 left-[calc(100%+8px)] flex max-h-[360px] w-[300px] animate-[fadeIn_140ms_ease-out_both] flex-col gap-2 overflow-y-auto rounded-2xl border border-white/[0.06] bg-[#353535] p-2 shadow-2xl"
          style={STUDIO_LIVE_VIEWPORT_VARS_RESET}
        >
          {SECTIONS.map(section => groups[section].length > 0 && (
            <div key={section} className="flex flex-col gap-0.5">
              <div className="px-2 pb-0.5 pt-1 text-xs font-medium uppercase tracking-wider text-white/40">
                {STATUS_LABEL[section]} ({groups[section].length})
              </div>
              {groups[section].map(task => (
                <TaskRow
                  key={task.id}
                  task={task}
                  status={section}
                  isExpanded={expandedTaskId === task.id}
                  onToggle={handleToggleTask}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});
WorkspaceTaskMenu.displayName = 'WorkspaceTaskMenu';

export default WorkspaceTaskMenu;
